import {Button, Dropdown, DropdownGroup, DropdownItem} from 'platform/components';

import {isEmpty, isNotEmpty} from 'ramda';

import {
  SALE_VEHICLE_PHOTO_TYPES_BY_GROUP,
  SaleVehiclePhotoTypeGroupSchema,
  SlotSaleVehiclePhotoType,
  SlotSaleVehiclePhotoTypeSchema,
} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {getSaleVehiclePhotoTypeGroupLabel} from '../utils/getSaleVehiclePhotoTypeGroupLabel';
import {getSaleVehiclePhotoTypeLabel} from '../utils/getSaleVehiclePhotoTypeLabel';

const isSlotPhotoType = (photoType: string): photoType is SlotSaleVehiclePhotoType =>
  SlotSaleVehiclePhotoTypeSchema.safeParse(photoType).success;

interface PhotoTypePickerProps extends RequiredTestIdProps {
  usedPhotoTypes: SlotSaleVehiclePhotoType[];
  isDisabled: boolean;
  onSelect: (photoType: SlotSaleVehiclePhotoType) => void;
}

/** Offers the slot-eligible Photo Types not yet in the guide, grouped as in the codelist. */
export function PhotoTypePicker(props: PhotoTypePickerProps) {
  const availableGroups = SaleVehiclePhotoTypeGroupSchema.options
    .map((group) => ({
      group,
      photoTypes: SALE_VEHICLE_PHOTO_TYPES_BY_GROUP[group]
        .filter(isSlotPhotoType)
        .filter((photoType) => !props.usedPhotoTypes.includes(photoType)),
    }))
    .filter((item) => isNotEmpty(item.photoTypes));
  const isPickerDisabled = props.isDisabled || isEmpty(availableGroups);

  return (
    <Dropdown
      placement="bottom-start"
      isHeightLimited
      isDisabled={isPickerDisabled}
      dropdownControl={
        <Button
          variant="link"
          leftIcon="content/add_circle"
          title={i18n.t('page.salesSettings.photoGuide.actions.addSlot')}
          isDisabled={isPickerDisabled}
          data-testid={props['data-testid']}
        />
      }
      data-testid={suffixTestId('dropdown', props)}
    >
      {availableGroups.map((item) => (
        <DropdownGroup key={item.group} label={getSaleVehiclePhotoTypeGroupLabel(item.group)}>
          {item.photoTypes.map((photoType) => (
            <DropdownItem
              key={photoType}
              label={getSaleVehiclePhotoTypeLabel(photoType)}
              onClick={() => props.onSelect(photoType)}
              data-testid={suffixTestId(photoType, props)}
            />
          ))}
        </DropdownGroup>
      ))}
    </Dropdown>
  );
}
